import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuthStore } from "./lib/auth.js";
import { Shell } from "./components/layout/Shell.js";
import { LoginPage } from "./pages/Login.js";
import { ChangePasswordPage } from "./pages/ChangePassword.js";
import { HomePage } from "./pages/Home.js";
import { CompaniesPage } from "./pages/Companies.js";
import { CompanyDetailPage } from "./pages/CompanyDetail.js";
import { SessionsPage } from "./pages/Sessions.js";
import { AgentsPage } from "./pages/Agents.js";
import { TemplatesPage } from "./pages/Templates.js";
import { ReportingPage } from "./pages/Reporting.js";
import { UsersPage } from "./pages/Users.js";
import { ChannelHealthPage } from "./pages/ChannelHealth.js";
import { AuditPage } from "./pages/Audit.js";
import { SettingsPage } from "./pages/Settings.js";
// El módulo Webchats se carga aparte: quien no lo usa no baja su código.
const WebchatsListPage = lazy(() => import("./pages/webchats/WebchatsList.js").then((m) => ({ default: m.WebchatsListPage })));
const WebchatNewPage = lazy(() => import("./pages/webchats/WebchatNew.js").then((m) => ({ default: m.WebchatNewPage })));
const WebchatDetailPage = lazy(() => import("./pages/webchats/WebchatDetail.js").then((m) => ({ default: m.WebchatDetailPage })));
const WebchatOperacionPage = lazy(() =>
  import("./pages/webchats/WebchatOperacion.js").then((m) => ({ default: m.WebchatOperacionPage })),
);

/** Una pantalla que exige un permiso: sin él, vuelve al inicio. Carga su código cuando se abre. */
function RequirePermission({ perm, children }: Readonly<{ perm: string; children: JSX.Element }>) {
  const allowed = useAuthStore((s) => s.user?.permissions.includes(perm) ?? false);
  if (!allowed) return <Navigate to="/" replace />;
  return (
    <Suspense
      fallback={
        <div className="p-12 grid place-items-center text-slate-400">
          <Loader2 className="animate-spin" size={20} aria-label="Cargando" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const token = useAuthStore((s) => s.accessToken);
  const mustChange = useAuthStore((s) => s.mustChangePassword);
  if (!token) return <Navigate to="/login" replace />;
  if (mustChange) return <Navigate to="/change-password" replace />;
  return children;
}

function RequireAuthOnly({ children }: { children: JSX.Element }) {
  const token = useAuthStore((s) => s.accessToken);
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/change-password"
        element={
          <RequireAuthOnly>
            <ChangePasswordPage />
          </RequireAuthOnly>
        }
      />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="companies" element={<CompaniesPage />} />
        <Route path="companies/:id" element={<CompanyDetailPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="agents" element={<AgentsPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="reporting" element={<ReportingPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="channel-health" element={<ChannelHealthPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="webchats" element={<RequirePermission perm="webchat.read"><WebchatsListPage /></RequirePermission>} />
        <Route path="webchats/nuevo" element={<RequirePermission perm="webchat.write"><WebchatNewPage /></RequirePermission>} />
        <Route
          path="webchats/operaciones/:id"
          element={<RequirePermission perm="webchat.read"><WebchatOperacionPage /></RequirePermission>}
        />
        <Route path="webchats/:idcanal" element={<RequirePermission perm="webchat.read"><WebchatDetailPage /></RequirePermission>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
